import { forwardRef, type KeyboardEvent, type ReactNode } from "react";
import { GripHorizontal } from "lucide-react";

// 首頁那一塊小工具的外框(業主 2026-09-17:「參考iphone 或者是安卓小工具
// 那種作法…把整個首頁畫成很多小小的格子可以調整每一個工具的寬高」)。
//
// ==========================================================================
// 只有那顆握把拖得動
// ==========================================================================
// 整塊都能拖的話,在代辦事項裡選一段字就會變成把整個區塊拖走 ——
// 而那一塊裡本來就有輸入框、下拉與按鈕。iOS 的小工具是長按才進編輯模式,
// 網頁上對應的做法就是給一顆明確的握把。
//
// ==========================================================================
// 拖曳交給版面元件,**鍵盤自己接**
// ==========================================================================
// react-grid-layout 負責滑鼠與觸控,但它沒有鍵盤操作 —— 不用滑鼠的人
// 就會被鎖在預設版面裡,而版面是他每天第一眼看到的東西。
// 所以左下角那顆鈕是一顆真的按鈕:focus 得到、吃方向鍵。
// 它與版面元件自己的縮放把手(右下角)刻意不在同一個角落 ——
// 疊在一起的話,滑鼠永遠點到上面那一顆。
//
// ==========================================================================
// 這個元件要能被 react-grid-layout 包起來
// ==========================================================================
// 它會把 `style`、`className`、滑鼠事件與一個 ref **注入到子元素上**
// (位置是 transform,不是 grid-area)。所以這裡一律 forwardRef,
// 而且把收到的 props 原樣放行 —— 少放一個,那一塊就拖不動或擺錯位置。

export interface HomeBlockProps {
  id: string;
  /** 把手的無障礙名稱用它:「調整「日曆」的大小」。 */
  title: string;
  onResize?: (dw: number, dh: number) => void;
  children?: ReactNode;
  /** 以下都是 react-grid-layout 注入的,原樣放行。 */
  className?: string;
  style?: React.CSSProperties;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchStart?: React.TouchEventHandler;
  onTouchEnd?: React.TouchEventHandler;
}

export const HomeBlock = forwardRef<HTMLDivElement, HomeBlockProps>(function HomeBlock(
  { id, title, onResize, children, className = "", style, ...rest },
  ref,
) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowRight: [1, 0],
      ArrowLeft: [-1, 0],
      ArrowDown: [0, 1],
      ArrowUp: [0, -1],
    };
    const move = moves[event.key];
    if (!move || !onResize) return;
    // 方向鍵在頁面上本來是捲動。焦點在這顆鈕上時它的意思是「改大小」,
    // 兩件事同時發生的話,調一格畫面就跳一次。
    event.preventDefault();
    onResize(move[0], move[1]);
  };

  return (
    <div
      ref={ref}
      data-block={id}
      className={`${className} flex min-w-0 flex-col`}
      style={style}
      {...rest}
    >
      {/* 把手。`nq-drag` 是 react-grid-layout 認的那個選擇器 ——
        // 名字改了就拖不動,所以它與 HomePage 那一行設定要一起看。
          **浮在卡片上面,不是卡片上面再疊一條。** 每一塊裡面本來就是一張
          有圓角與外框的卡片;在它上方再放一條長方形的握把,兩者之間會出現
          一條接不起來的縫。做成一顆浮在頂端中央的小藥丸就沒有這個問題,
          而且更像手機桌面上長按之後出現的那個握把。 */}
      <div
        className={
          "nq-drag absolute left-1/2 top-1 z-10 flex -translate-x-1/2 cursor-grab items-center " +
          "gap-1 rounded-full border border-[var(--border)] bg-[var(--surface-2)] px-2 py-0.5 " +
          "text-[var(--text-3)] opacity-60 hover:opacity-100 active:cursor-grabbing"
        }
        // 這一顆是滑鼠用的握把,左下角那顆鈕才是鍵盤的入口。
        aria-hidden="true"
        title={`拖曳可以搬動「${title}」`}
      >
        <GripHorizontal size={14} />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">{children}</div>

      {!onResize ? null : (
        <button
          type="button"
          aria-label={`調整「${title}」的大小`}
          // 說得出怎麼用 —— 一個只有斜線的小三角,沒有人知道它吃方向鍵。
          title="用方向鍵調整大小(拖右下角也可以)"
          onKeyDown={onKeyDown}
          className={
            "absolute bottom-0 left-0 h-5 w-5 rounded-tr-[var(--radius)] " +
            "border-r border-t border-[var(--border)] bg-[var(--surface-2)] " +
            "text-[var(--text-3)] hover:bg-[var(--surface)] hover:text-[var(--text)] " +
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
          }
        >
          <span aria-hidden="true" className="block text-[10px] leading-[1.25rem]">
            ⇱
          </span>
        </button>
      )}
    </div>
  );
});
